import { t } from "@travily/i18n";
import { searchableListBox } from "@ui/components/internal/SearchableListBox";
import { ScrollArea } from "@ui/components/ScrollArea";
import { TagGroup, type TagGroupColor } from "@ui/components/TagGroup";
import { tv, type VariantProps } from "@ui/lib/tv";
import {
  Autocomplete as RACAutocomplete,
  SearchFieldInput as RACInput,
  SearchField as RACSearchField,
} from "@ui/primitives/Autocomplete";
import { Button as RACButton } from "@ui/primitives/Button";
import {
  ListBox as RACListBox,
  ListBoxItem as RACListBoxItem,
  type ListBoxItemProps as RACListBoxItemProps,
} from "@ui/primitives/ListBox";
import { Popover as RACPopover } from "@ui/primitives/Popover";
import {
  Select as RACSelect,
  type SelectProps as RACSelectProps,
  SelectValue as RACSelectValue,
} from "@ui/primitives/Select";
import { composeRenderProps, useFilter } from "@ui/primitives/shared";
import {
  TextFieldGroup as RACGroup,
  TextFieldLabel as RACLabel,
  TextFieldText as RACText,
} from "@ui/primitives/TextField";
import { createContext, type ReactNode, type RefObject, use, useEffect, useMemo, useRef, useState } from "react";

const select = tv({
  slots: {
    root: "flex flex-col gap-1",
    label: "text-lg/6 font-semibold text-neutral-900",
    trigger: [
      "group flex items-center gap-1.5 rounded-xl bg-white outline-none text-left",
      "shadow-keyline",
      "data-[focus-within]:shadow-[0_0_0_1px_var(--color-blue-400),0_0_0_4px_var(--color-blue-200)]",
      "data-[disabled]:cursor-not-allowed data-[disabled]:bg-neutral-50",
    ],
    leadIcon: "shrink-0 self-center",
    valueArea: "flex flex-1 flex-wrap items-center gap-1 self-stretch min-w-0",
    placeholder: "self-center text-neutral-400",
    singleValue: "self-center truncate text-neutral-900",
    chevron: [
      "inline-flex shrink-0 cursor-pointer items-center justify-center text-neutral-400 outline-none",
      "self-center rounded-md",
      "data-[focus-visible]:shadow-[0_0_0_2px_var(--color-blue-400)]",
      "transition-transform duration-150 group-data-[open]:rotate-180",
    ],
    popover: [
      "rounded-2xl bg-white p-2 outline-none",
      "shadow-[0_3px_10px_0_rgba(0,0,0,0.1),0_1px_0_0_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.05)]",
      "w-(--trigger-width) max-h-[320px] flex flex-col gap-2",
      "entering:animate-in entering:fade-in entering:zoom-in-95 entering:duration-100",
      "exiting:animate-out exiting:fade-out exiting:zoom-out-95 exiting:duration-75",
    ],
    item: [
      "flex cursor-pointer items-center gap-2 rounded-xl px-3 py-2 outline-none",
      "text-neutral-700 transition-colors",
      "data-[hovered]:bg-neutral-100 data-[focused]:bg-neutral-100",
      "data-[selected]:bg-neutral-100 data-[selected]:font-semibold data-[selected]:text-neutral-700",
      "data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50",
    ],
    valueWrapper: "contents",
    message: "pt-0.5 text-sm/5 text-secondary",
  },
  variants: {
    size: {
      sm: {
        trigger: "min-h-10 px-2 py-1",
        leadIcon: "[&_svg]:size-4",
        placeholder: "text-sm",
        singleValue: "text-sm",
        chevron: "size-5 [&_svg]:size-4",
        item: "px-2 py-1.5 text-sm",
      },
      md: {
        trigger: "min-h-11 px-2 py-1.5",
        leadIcon: "[&_svg]:size-5",
        placeholder: "text-base",
        singleValue: "text-base",
        chevron: "size-5 [&_svg]:size-4",
        item: "px-2.5 py-1.5 text-sm",
      },
      lg: {
        trigger: "min-h-12 px-2 py-1.5",
        leadIcon: "[&_svg]:size-6",
        placeholder: "text-base",
        singleValue: "text-base",
        chevron: "size-5 [&_svg]:size-5",
        item: "px-3 py-2 text-base",
      },
      xl: {
        trigger: "min-h-[52px] px-2.5 py-2",
        leadIcon: "[&_svg]:size-6",
        placeholder: "text-lg",
        singleValue: "text-lg",
        chevron: "size-6 [&_svg]:size-5",
        item: "px-3 py-2 text-base",
      },
    },
    isInvalid: {
      true: {
        trigger: "shadow-[0_0_0_1px_var(--color-pink-500),0_0_0_4px_var(--color-pink-100)]",
      },
    },
    // Opt-in styling that matches the WizardTextField family: a soft white field with a
    // 1px ring + bottom shadow, a green focus halo, and the wizard's larger metrics, so a
    // Select sits alongside the wizard's text fields as one field system.
    variant: {
      default: {},
      wizard: {
        label: "text-xl/6",
        trigger: [
          "min-h-14 rounded-3xl px-4 lg:min-h-16 lg:px-5",
          "shadow-[0_0_0_1px_rgba(0,0,0,0.12),0_1px_0_0_rgba(0,0,0,0.16)]",
          "transition duration-100 ease-out",
          "data-[focus-within]:shadow-[0_0_0_8px_var(--color-green-200),0_0_0_2px_var(--color-green-500)]",
          "data-[open]:shadow-[0_0_0_8px_var(--color-green-200),0_0_0_2px_var(--color-green-500)]",
        ],
        placeholder: "text-xl text-neutral-500",
        singleValue: "text-xl",
        chevron: "data-[focus-visible]:shadow-[0_0_0_2px_var(--color-green-500)]",
      },
    },
  },
  compoundVariants: [
    // Invalid on the wizard field reuses the wizard focus geometry in pink, so the ring
    // never changes size between focus and invalid — matching WizardTextField.
    {
      variant: "wizard",
      isInvalid: true,
      class: { trigger: "shadow-[0_0_0_8px_var(--color-pink-200),0_0_0_2px_var(--color-pink-500)]" },
    },
  ],
  defaultVariants: { size: "lg", variant: "default" },
});

type SelectVariants = VariantProps<typeof select>;
export type SelectSize = NonNullable<SelectVariants["size"]>;
export type SelectVariant = NonNullable<SelectVariants["variant"]>;
export type SelectionMode = "single" | "multiple";

interface SelectContextValue {
  size: SelectSize;
  registerItem: (id: string, textValue: string) => void;
}

const SelectContext = createContext<SelectContextValue>({ size: "lg", registerItem: () => {} });

const TAG_COLORS = ["yellow", "blue", "green"] as const satisfies readonly TagGroupColor[];

function tagColorAt(index: number): TagGroupColor {
  const safeIndex = index < 0 ? 0 : index % TAG_COLORS.length;
  return TAG_COLORS[safeIndex] ?? "yellow";
}

// RAC Select sets `--trigger-width` from its internal Button (the small chevron).
// We anchor the popover to the surrounding Group, so we measure the Group width
// ourselves and set it explicitly on the popover.
function useTriggerWidth(ref: RefObject<HTMLElement | null>) {
  const [width, setWidth] = useState<number | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const measure = () => setWidth(element.getBoundingClientRect().width);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);

  return width;
}

function tagSizeFor(size: SelectSize): SelectSize {
  // Tags inline within the trigger should be one step smaller than the trigger
  // so they don't fill the whole trigger height.
  if (size === "xl") return "lg";
  if (size === "lg") return "md";
  if (size === "md") return "sm";
  return "sm";
}

interface SelectItem {
  id: string;
  textValue?: string;
}

export interface SelectProps<M extends SelectionMode = "single">
  extends Omit<RACSelectProps<SelectItem, M>, "children"> {
  children: ReactNode;
  description?: ReactNode;
  errorMessage?: ReactNode;
  isOptional?: boolean;
  label?: string;
  /** Visually hide the label (kept for assistive tech). Use when a stage heading
   *  already names the field, as in the wizard flows. */
  isLabelHidden?: boolean;
  leadIcon?: ReactNode;
  placeholder?: string;
  size?: SelectSize;
  variant?: SelectVariant;
}

function SelectRoot<M extends SelectionMode = "single">({
  children,
  description,
  errorMessage,
  isInvalid,
  isOptional: _isOptional,
  label,
  isLabelHidden,
  leadIcon,
  placeholder,
  size = "lg",
  variant = "default",
  selectionMode,
  ...props
}: SelectProps<M>) {
  const { contains } = useFilter({ sensitivity: "base" });
  const styles = select({ size, isInvalid, variant });
  const listStyles = searchableListBox({ field: "boxed" });
  const showErrorMessage = isInvalid && errorMessage;

  const triggerRef = useRef<HTMLDivElement>(null);
  const triggerWidth = useTriggerWidth(triggerRef);
  const itemTextValues = useRef<Map<string, string>>(new Map());
  const contextValue = useMemo<SelectContextValue>(
    () => ({
      size,
      registerItem: (id, textValue) => {
        itemTextValues.current.set(id, textValue);
      },
    }),
    [size],
  );

  const tagSize = tagSizeFor(size);
  const searchPlaceholder = t("components.select.search_placeholder");

  return (
    <SelectContext value={contextValue}>
      <RACSelect
        isInvalid={isInvalid}
        selectionMode={selectionMode}
        {...props}
        className={composeRenderProps(props.className, (className) => styles.root({ className }))}
      >
        {label ? <RACLabel className={isLabelHidden ? "sr-only" : styles.label()}>{label}</RACLabel> : null}
        <SelectTrigger className={styles.trigger()} ref={triggerRef}>
          {leadIcon ? (
            <span aria-hidden="true" className={styles.leadIcon()}>
              {leadIcon}
            </span>
          ) : null}
          <RACSelectValue<SelectItem> className={composeRenderProps("", () => styles.valueArea())}>
            {({ selectedItems, state, isPlaceholder }) => {
              if (selectionMode === "multiple") {
                const keys = Array.isArray(state.value) ? state.value : [];
                if (keys.length === 0) {
                  return placeholder ? <span className={styles.placeholder()}>{placeholder}</span> : null;
                }

                const items = keys.map((key, index) => {
                  const id = String(key);
                  const fromCollection = selectedItems[index];
                  return {
                    id,
                    textValue: fromCollection?.textValue ?? itemTextValues.current.get(id) ?? id,
                  };
                });

                return (
                  <TagGroup
                    aria-label={t("components.select.selected_items")}
                    items={items}
                    onRemove={(removedKeys) => {
                      for (const key of removedKeys) {
                        state.selectionManager.toggleSelection(key);
                      }
                    }}
                    size={tagSize}
                  >
                    {(item) => (
                      <TagGroup.Tag color={tagColorAt(items.findIndex((entry) => entry.id === item.id))} id={item.id}>
                        {item.textValue}
                      </TagGroup.Tag>
                    )}
                  </TagGroup>
                );
              }

              if (isPlaceholder) {
                return placeholder ? <span className={styles.placeholder()}>{placeholder}</span> : null;
              }

              // RAC resolves the selected item's label from the (Autocomplete-filtered) collection,
              // so a selection made under an active search filter can come back without its
              // textValue. Fall back to the name we registered when the item rendered, then the key.
              const single = selectedItems[0];
              const display =
                single?.textValue ?? itemTextValues.current.get(String(state.selectedKey)) ?? state.selectedKey ?? "";
              return <span className={styles.singleValue()}>{display}</span>;
            }}
          </RACSelectValue>
          <RACButton className={styles.chevron()}>
            <ChevronDownIcon />
          </RACButton>
        </SelectTrigger>
        <RACPopover
          className={styles.popover()}
          style={triggerWidth != null ? { width: triggerWidth } : undefined}
          triggerRef={triggerRef}
        >
          <RACAutocomplete filter={contains}>
            <RACSearchField aria-label={searchPlaceholder} className={listStyles.searchField()}>
              <RACInput autoFocus className={listStyles.searchInput()} placeholder={searchPlaceholder} />
            </RACSearchField>
            <ScrollArea
              className="flex-1"
              inset="none"
              render={
                <RACListBox
                  renderEmptyState={() => (
                    <div className={listStyles.empty({ className: "px-3" })}>{t("components.select.no_results")}</div>
                  )}
                />
              }
              viewportClassName={listStyles.list()}
            >
              {children}
            </ScrollArea>
          </RACAutocomplete>
        </RACPopover>
        {showErrorMessage ? (
          <RACText className={styles.message()} slot="errorMessage">
            {errorMessage}
          </RACText>
        ) : description ? (
          <RACText className={styles.message()} slot="description">
            {description}
          </RACText>
        ) : null}
      </RACSelect>
    </SelectContext>
  );
}

export interface SelectItemProps extends Omit<RACListBoxItemProps, "children"> {
  children: ReactNode;
}

function SelectItem({ children, ...props }: SelectItemProps) {
  const { size, registerItem } = use(SelectContext);
  const styles = select({ size });
  const textValue = typeof children === "string" ? children : props.textValue;

  if (props.id != null && textValue) {
    registerItem(String(props.id), textValue);
  }

  return (
    <RACListBoxItem
      textValue={textValue}
      {...props}
      className={composeRenderProps(props.className, (className) => styles.item({ className }))}
    >
      {children}
    </RACListBoxItem>
  );
}

interface SelectTriggerProps {
  children: ReactNode;
  className?: string;
  ref?: RefObject<HTMLDivElement | null>;
}

function SelectTrigger({ children, className, ref }: SelectTriggerProps) {
  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    // Pass through pointer events on interactive descendants.
    if (target.closest('button, [role="button"], [role="row"], [role="rowheader"], a, input')) return;
    // Forward to the chevron button so RAC manages the open/close state correctly.
    const button = event.currentTarget.querySelector("button");
    if (!button) return;
    event.preventDefault();
    button.click();
  };

  return (
    <RACGroup className={className} onPointerDown={handlePointerDown} ref={ref}>
      {children}
    </RACGroup>
  );
}

function ChevronDownIcon() {
  return (
    <svg aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export const Select = Object.assign(SelectRoot, { Item: SelectItem });
